import React, { useState } from "react";
import { EnvelopeSimple, X } from "@phosphor-icons/react";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";

/**
 * EmailVerificationBanner — shown at the top of the dashboard when a logged-in
 * user has not yet verified their email. One-click "resend email" action.
 */
export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);

  // Only show for logged-in, unverified, non-admin users
  if (!user || user === false) return null;
  if (user.email_verified) return null;
  if (user.role === "admin") return null;
  if (dismissed) return null;

  const resend = async () => {
    setSending(true);
    setMsg(null);
    try {
      await api.post("/auth/request-verification");
      setMsg("Verification email sent. Check your inbox.");
    } catch {
      setMsg("Could not send right now. Try again from your account page.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="email-verify-banner"
      className="border-b border-tac-warning/40 bg-tac-warning/8 px-4 py-2.5 flex items-center gap-3 flex-wrap"
    >
      <EnvelopeSimple size={16} weight="duotone" className="text-tac-warning shrink-0" />
      <div className="flex-1 min-w-0 text-xs text-tac-text">
        <strong className="text-tac-warning uppercase tracking-wider text-[10px] mr-2">Confirm your email</strong>
        Posting field updates and enabling push alerts unlocks once you verify <span className="font-mono-tac">{user.email}</span>.
        {msg && <span data-testid="email-verify-msg" className="block sm:inline ml-0 sm:ml-2 mt-1 sm:mt-0 text-tac-dim">{msg}</span>}
      </div>
      <button
        data-testid="email-verify-resend"
        onClick={resend}
        disabled={sending}
        className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-tac-warning/15 hover:bg-tac-warning/25 disabled:opacity-50 text-tac-warning border border-tac-warning/55 rounded-sm transition-colors shrink-0"
      >
        {sending ? "Sending…" : "Resend"}
      </button>
      <button
        data-testid="email-verify-dismiss"
        onClick={() => setDismissed(true)}
        className="text-tac-warning/70 hover:text-tac-warning shrink-0"
        aria-label="Dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
